import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { trackCTAClick, trackThoughtSelect, trackPreviewStart } from '../utils/analytics';

const thoughtOptions = [
  "I can't stop thinking about this",
  'I feel stuck',
  "I don't know what to do",
  'I feel lost',
  "I'm not sure I'm in the right direction",
  'I keep going in circles',
  'I need clarity but I don’t know where to find it',
  'Something else...'
];

export default function ClarityPage() {
  const navigate = useNavigate();
  const [selectedThoughts, setSelectedThoughts] = useState([thoughtOptions[0]]);

  const toggleThought = (thought) => {
    trackThoughtSelect(thought, '/clarity');
    setSelectedThoughts((prev) =>
      prev.includes(thought) ? prev.filter((item) => item !== thought) : [...prev, thought]
    );
  };

  return (
    <>
      <Helmet>
        <title>What Has Been On Your Mind Lately? | Andlegur</title>
        <meta
          name="description"
          content="Choose the thought that matches what you feel, then start your reflection with Andlegur."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/clarity" />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#05091B] font-serif text-indigo-100">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="/web_images/Andlegur%20web%2028.webp"
            alt=""
            className="h-full w-full object-cover opacity-16 mix-blend-screen"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/40 via-transparent to-[#05091B]" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/60" />
        </div>

        <div className="relative z-10 min-h-screen px-4 sm:px-8 py-8 sm:py-10 flex items-center justify-center">
          <section className="w-full max-w-4xl rounded-3xl border border-indigo-400/50 bg-[#05091B] p-5 sm:p-8 lg:p-10 backdrop-blur-md shadow-2xl text-center">
            <div className="mb-5 sm:mb-6">
              <Link to="/" className="text-indigo-300 hover:text-white text-base sm:text-lg tracking-wide transition-colors">
                Andlegur
              </Link>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white">
              What has been on
              <br />
              your mind lately?
            </h1>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-left">
              {thoughtOptions.map((thought) => {
                const isSelected = selectedThoughts.includes(thought);
                return (
                  <button
                    key={thought}
                    onClick={() => toggleThought(thought)}
                    className={`relative rounded-xl border px-3.5 py-2.5 pr-10 text-sm sm:text-base leading-snug transition-all duration-200 text-left ${
                      isSelected
                        ? 'border-blue-300/70 bg-[#1A4BA0]/55 text-white shadow-lg shadow-blue-900/35'
                        : 'border-indigo-300/30 bg-[#11214A]/70 text-indigo-100 hover:border-blue-300/60 hover:bg-[#172B5C]/75'
                    }`}
                    aria-pressed={isSelected}
                  >
                    <span
                      className={`absolute top-2 right-2 h-5 w-5 rounded border flex items-center justify-center text-[11px] font-bold ${
                        isSelected
                          ? 'bg-blue-500 border-blue-300 text-white'
                          : 'bg-white/90 border-blue-300/70 text-blue-500'
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? '✓' : ''}
                    </span>
                    {thought}
                  </button>
                );
              })}
            </div>

            <p className="mt-6 text-indigo-100 text-base sm:text-lg leading-relaxed">
              Start making sense of it.
            </p>

            <p className="mt-2 text-blue-300 text-sm sm:text-base font-semibold">
              Continue with {selectedThoughts.length} thought{selectedThoughts.length === 1 ? '' : 's'} and find clarity.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => { trackCTAClick('start_my_reflection', '/clarity'); navigate('/join'); }}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-base sm:text-lg font-semibold px-7 py-3.5 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-indigo-700/35"
              >
                Start My Reflection {'->'}
              </button>
              <button
                onClick={() => { trackCTAClick('try_insight_preview', '/clarity'); trackPreviewStart(); navigate('/try'); }}
                className="w-full sm:w-auto border border-indigo-300/55 text-indigo-100 hover:bg-indigo-900/35 text-base sm:text-lg font-semibold px-7 py-3.5 rounded-2xl transition-all duration-300"
              >
                Try Insight Preview {'->'}
              </button>
            </div>

            <p className="mt-4 text-indigo-300 text-xs sm:text-sm">Private | Free | No pressure</p>

            <div className="md:hidden mt-6 relative overflow-hidden rounded-2xl border border-indigo-300/30">
              <img
                src="/web_images/Andlegur%20web%2028.webp"
                alt="A person processing thoughts and looking for clear direction"
                className="h-36 w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-linear-to-t from-[#05091B]/90 via-[#05091B]/35 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-center text-white text-sm font-semibold tracking-wide">
                Find Your Directions. Find Clarity
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
